import React, {Component} from 'react';
import t from 'babel-plugin-yate/macro';
import {connect} from 'react-redux';
import {Form, Field} from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import {FieldArray} from 'react-final-form-arrays';

import Email from '../../components/Email';

import {getEmploymentStatuses, getInstallments} from '../../store/request';

import {
  composeFormValidators,
  isRequired,
  isAlpha,
  isCNP,
  isNumber,
  hasMaxLength,
  isPhoneNumber,
  hasItems,
  isNumberWithTwoDecimals,
  isAlphaNumeric,
} from '../../lib/validations';

import {
  ANAF_CODE,
  MAI_MAPN_CODE,
  PENSIONARY_CODE,
} from '../../config/employmentStatuses';

import {
  formatWhiteSpaceFormField,
  parseNumberFormField,
  parseNumberIntegerFormField,
} from '../../lib/misc';

import {Input} from '../../components/Form/Input';
import {Select} from '../../components/Form/Select';
import ValidationError from '../../components/Form/ValidationError';
import Checkbox from '../../components/Form/Checkbox';
import {Block} from '../../components/uiElem';

const hasProfessionalStatusPensionerValue = fieldValue =>
  fieldValue?.[0]?.code === PENSIONARY_CODE;

class ReceivedDetailsForm extends Component {
  state = {
    installments: [],
    isLoadingInstallments: true,
    isEmailValidated: false,
  };

  async componentDidMount() {
    const {employmentStatuses, getEmploymentStatuses, uuid} = this.props;

    await this.getInstallments();

    if (!employmentStatuses) {
      await getEmploymentStatuses(uuid);
    }
  }

  getInstallments = async () => {
    const {
      initialValues: {location_id},
      getInstallments,
      uuid,
    } = this.props;

    this.setState({
      isLoadingInstallments: true,
    });

    const installmentsResponse = await getInstallments(location_id, uuid);

    if (!installmentsResponse.error) {
      this.setState({
        installments: installmentsResponse,
        isLoadingInstallments: false,
      });
    }
  };

  setIsEmailValidated = isEmailValidated => {
    this.setState({isEmailValidated});
  };

  render() {
    const {
      disabled,
      employmentStatuses,
      handleAction,
      initialValues,
      formRef,
      uuid,
    } = this.props;

    const {installments, isLoadingInstallments} = this.state;
    const isConsumerFinance = initialValues?.type === 4;
    const isPersonalNeeds = initialValues?.type === 5;
    const isCreditCard = initialValues?.type === 6;

    return (
      <Block
        heading={t`Personal data`}
        description={t`Fill in and review your personal data to go to the next step`}
        descriptionClassName="jupiter">
        <Form
          keepDirtyOnReinitialize={true}
          onSubmit={(values, form) => {
            /**
             * 'monthly_income' or 'pension' fields can be set to 'null'
             * when the employment status is changed from the UI.
             *
             * if the checked employment status has the value of ANAF_CODE or MAI_MAPN_CODE,
             * the 'pension' field is set to 'null' and 'monthly_income' field is populated.
             *
             * if the checked employment status has the value of PENSIONARY_CODE,
             * the 'monthly_income' field is set to 'null' and 'pension' field is populated.
             * */

            handleAction({
              ...values,
              customer: {
                ...values.customer,
                employment: {
                  ...values.customer?.employment,
                  ...(hasProfessionalStatusPensionerValue(
                    form.getFieldState('customer.professional_status')?.value,
                  )
                    ? {
                        monthly_income: null,
                      }
                    : {
                        pension: null,
                      }),
                },
              },
            });
          }}
          initialValues={initialValues}
          mutators={arrayMutators}
          validate={() => {
            if (disabled) {
              return;
            }

            const customErrors = {
              customer: {
                email_validation_warn: undefined,
              },
            };

            if (!this.state.isEmailValidated) {
              customErrors.customer.email_validation_warn = t`The email must be validated!`;
            }

            return customErrors;
          }}
          render={({handleSubmit, values, errors, form}) => {
            return (
              <form onSubmit={handleSubmit} ref={formRef}>
                <div className="pt-8 p-2">
                  <div className="md:flex md:flex-wrap">
                    <div className="w-full md:w-1/2 px-2 mb-4">
                      <Field
                        name="customer.identity_card.cnp"
                        label={t`Social Security no`}
                        component={Input}
                        disabled={
                          disabled ||
                          (isPersonalNeeds &&
                            initialValues.customer?.identity_card?.cnp)
                        }
                        type="number"
                        validate={composeFormValidators(isRequired(), isCNP())}
                      />
                    </div>

                    <div className="w-full md:w-1/2 px-2 mb-4">
                      <Field
                        name="customer.mobile_phone"
                        label={t`Mobile number`}
                        component={Input}
                        disabled={
                          disabled ||
                          (isPersonalNeeds &&
                            initialValues.customer?.identity_card?.cnp)
                        }
                        format={formatWhiteSpaceFormField}
                        validate={composeFormValidators(
                          isRequired(),
                          hasMaxLength(10),
                          isPhoneNumber(),
                        )}
                      />
                    </div>

                    <div className="w-full md:w-1/2 px-2 mb-4">
                      <Field
                        name="customer.last_name"
                        label={t`Last name`}
                        component={Input}
                        disabled={disabled}
                        validate={composeFormValidators(
                          isRequired(),
                          isAlpha(),
                          hasMaxLength(64),
                        )}
                      />
                    </div>

                    <div className="w-full md:w-1/2 px-2 mb-4">
                      <Field
                        name="customer.first_name"
                        label={t`First name`}
                        component={Input}
                        disabled={disabled}
                        validate={composeFormValidators(
                          isRequired(),
                          isAlpha(),
                          hasMaxLength(64),
                        )}
                      />
                    </div>

                    <div className="w-full px-2 mb-4">
                      <Email
                        uuid={uuid}
                        status={initialValues.status}
                        customer={values?.customer}
                        disabled={
                          disabled ||
                          (isPersonalNeeds &&
                            initialValues.customer?.identity_card?.cnp)
                        }
                        disableValidationBtn={
                          disabled ||
                          errors?.customer?.email ||
                          !values.customer?.identity_card?.cnp ||
                          errors?.customer?.identity_card?.cnp
                        }
                        handlerSetIsEmailValidated={this.setIsEmailValidated}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-8 p-2">
                  <div className="md:flex md:flex-wrap">
                    <div className="w-full md:w-1/2 px-2 mb-4">
                      <Field
                        name="detail.loan_amount"
                        label={
                          !isCreditCard
                            ? t`Total credit value`
                            : 'Limita card solicitata'
                        }
                        component={Input}
                        disabled={disabled || isConsumerFinance}
                        type="number"
                        parse={parseNumberFormField}
                        validate={composeFormValidators(
                          isRequired(),
                          isNumberWithTwoDecimals(),
                        )}
                      />
                    </div>

                    {!isCreditCard && (
                      <div className="w-full md:w-1/2 px-2 mb-4">
                        <Field
                          name="detail.installments"
                          label={t`Duration in months`}
                          component={Select}
                          validate={isRequired()}
                          parse={parseNumberFormField}
                          loadingData={isLoadingInstallments}>
                          <option value="">{t`Choose`}</option>
                          {installments?.map(el => (
                            <option key={el} value={el}>
                              {t`${el} months`}
                            </option>
                          ))}
                        </Field>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t pt-8 p-2">
                  <div className="md:flex md:flex-wrap">
                    <div className="w-full md:w-1/2 px-2 mb-4">
                      <Field
                        name={
                          hasProfessionalStatusPensionerValue(
                            form.getFieldState('customer.professional_status')
                              ?.value,
                          )
                            ? 'customer.employment.pension'
                            : 'customer.employment.monthly_income'
                        }
                        label={t`Monthly net income`}
                        component={Input}
                        type="number"
                        parse={parseNumberIntegerFormField}
                        disabled={disabled === 'all'}
                        validate={composeFormValidators(
                          isRequired(),
                          isNumber(),
                        )}
                      />

                      <div className="w-full mt-4">
                        <FieldArray
                          name="customer.professional_status"
                          validate={hasItems()}>
                          {({fields, meta}) => (
                            <>
                              {employmentStatuses &&
                                employmentStatuses.map(({id, name}) => {
                                  const fieldValueIndex =
                                    fields.value.findIndex(
                                      ({code}) => code === id,
                                    );

                                  const isFieldDisabled =
                                    Array.isArray(fields.value) &&
                                    // An empty array can't make the field disabled
                                    fields.value.length > 0 &&
                                    fields.value.findIndex(
                                      ({code}) => code === id,
                                    ) === -1;

                                  return (
                                    <div
                                      className={`w-full py-1 ${
                                        isFieldDisabled
                                          ? 'text-grey-darkest opacity-50'
                                          : ''
                                      } ${
                                        id === ANAF_CODE
                                          ? 'order-1'
                                          : id === MAI_MAPN_CODE
                                            ? 'order-3'
                                            : 'order-1'
                                      }`}
                                      key={id}>
                                      <Checkbox
                                        className="border-grey cursor-pointer"
                                        label={name}
                                        disabled={isFieldDisabled}
                                        checked={
                                          fieldValueIndex !== -1
                                            ? 'checked'
                                            : false
                                        }
                                        onChange={({target: {checked}}) =>
                                          checked
                                            ? fields.push({
                                                code: id,
                                              })
                                            : fields.remove(fieldValueIndex)
                                        }
                                      />
                                    </div>
                                  );
                                })}

                              {meta.error && meta.touched && (
                                <ValidationError value={meta.error} />
                              )}
                            </>
                          )}
                        </FieldArray>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-2 pt-8 border-t">
                  <div className="flex flex-wrap pb-5">
                    <div className="px-2 w-full md:w-1/2">
                      <Field
                        name="recommended_by"
                        label={t`Recommended brand code`}
                        component={Input}
                        format={formatWhiteSpaceFormField}
                        validate={composeFormValidators(
                          isAlphaNumeric(),
                          hasMaxLength(255),
                        )}
                      />
                    </div>
                  </div>
                </div>
              </form>
            );
          }}
        />
      </Block>
    );
  }
}

export default connect(
  ({request: {employmentStatuses}}) => ({
    employmentStatuses,
  }),
  {
    getEmploymentStatuses,
    getInstallments,
  },
)(ReceivedDetailsForm);
